import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { requests, send } from "../api/auth";
import { console_, type GettingStarted, type Today } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { stepHref } from "../system/gettingStarted";
import { SURFACE_PREFIX } from "../system/navigation";

// ---------------------------------------------------------------------------
// The overview screens for the three surfaces.
//
// V4 SHIPS A SHELL, SO THE OVERVIEW SAYS SO. There is no fake chart, no empty
// table headed "Upcoming events" and no card reading "0" for a thing that has
// not been built. A placeholder that looks like a feature is how a demo starts
// promising something the database cannot do, and it is how the person testing
// this stops trusting anything else on the page.
//
// Tour T-11, T-12 (owner, 20 October 2026): the session card, the status legend,
// the language panel and the permission list left the Overview. Language is in
// the console header; the rest was build-time scaffolding, not a scheduler's page.
// ---------------------------------------------------------------------------

/**
 * Tour T-11 (owner, 20 October 2026). What a scheduler opens the console for: four
 * counts from GET /v1/org/today, each a link to where it is dealt with. Nothing is
 * shown if the server will not answer (no event.read, no connection): the page
 * stands without it, as it does without Getting started.
 */
function TodayStrip() {
  const { apiBase } = useSession();
  const { t } = useT();
  const [today, setToday] = useState<Today | null>(null);
  useEffect(() => {
    let live = true;
    send<{ today: Today }>(console_.today(apiBase))
      .then(({ status, body }) => {
        if (live && status === 200) setToday(body.today);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [apiBase]);
  if (!today) return null;
  const events = `${SURFACE_PREFIX.console}/events`;
  const tiles: { n: number; label: string; to: string; attention: boolean }[] = [
    { n: today.events_today, label: t("events today"), to: events, attention: false },
    { n: today.unfilled, label: t("places to fill"), to: events, attention: today.unfilled > 0 },
    { n: today.not_arrived, label: t("not yet arrived"), to: events, attention: today.not_arrived > 0 },
    { n: today.to_approve, label: t("timesheets to approve"), to: events, attention: today.to_approve > 0 },
  ];
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Today")}</h2>
      </div>
      <ul className="today-strip">
        {tiles.map((tile) => (
          <li key={tile.label}>
            <Link to={tile.to} className={tile.attention ? "today-tile today-tile-attention" : "today-tile"}>
              <span className="today-n numeric">{tile.n}</span>
              <span>{tile.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * ★ GETTING STARTED — EVERY TICK IS THE DATABASE'S.
 *
 * This replaced `NextSteps`, which listed six links in the manual's order and
 * could not tell a fresh agency from one a year in: it showed "Add your first
 * client" to an organisation with forty. Sweep 3 October §3a walked a new
 * tenant's first hour and found the order to a paid shift written down only in
 * docs/manual/06-getting-started.md.
 *
 * THE SERVER DECIDES ALL OF IT. Whether each step is done is
 * `app.getting_started()` (0246) reading the organisation's own rows, nothing
 * here stores a tick, and whether the list shows at all — not finished, not put
 * away, something in it yours to do — is `show` in the answer. This component
 * draws, and turns each step's screen id into a path through the route table.
 *
 * A STEP YOU CANNOT DO SAYS WHO CAN instead of offering a link you would be
 * refused at.
 */
function GettingStartedList() {
  const { apiBase } = useSession();
  const { t, locale } = useT();
  const [list, setList] = useState<GettingStarted | null>(null);
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const { status, body } = await send<GettingStarted>(console_.gettingStarted(apiBase));
        if (live && status === 200 && Array.isArray(body.steps)) setList(body);
      } catch {
        // No list is the honest answer to no connection; the rest of the page stands.
      }
    })();
    return () => {
      live = false;
    };
  }, [apiBase]);

  if (!list?.show) return null;

  // R8. The answer is the same whatever the address (S21a), so "sent" is all it can say.
  async function resend(email: string) {
    setBusy(true);
    try {
      const { status } = await send(requests.requestEmailVerification(apiBase, email));
      setResent(status < 300 ? t("We sent the link again to {email}.", { email }) : t("The link could not be sent. Try again in a minute."));
    } catch {
      setResent(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  async function putAway() {
    setBusy(true);
    try {
      const { status } = await send(console_.setGettingStartedDismissed(apiBase, true));
      if (status === 200) setList((l) => (l ? { ...l, dismissed: true, show: false } : l));
    } catch {
      // Left showing: a list that vanished without being put away would come back on reload.
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Getting started")}</h2>
        <StatusChip tone="attention" label={t("{done} of {total} done", { done: list.required_done, total: list.required_total })} />
      </div>
      <p className="muted">
        {t("From signing up to a paid shift, in order — each step uses the one above it. A step ticks itself when the work is in Verbunk.")}
      </p>
      <ol className="plain-list step-list">
        {list.steps.map((step) => {
          // An action step is a button below, not a link to the page you are on.
          const href = step.can_act && !step.action ? stepHref(step) : null;
          return (
            <li key={step.key}>
              <StatusChip
                tone={step.done ? "live" : "neutral"}
                label={step.done ? t("Done") : step.optional ? t("Optional") : t("To do")}
              />
              {/* Tour T-14: the words are one block beside the chip, so a long line
                  wraps under its own start and never under the chip. */}
              <div>
              {href && !step.done ? (
                <a href={href}>{translateLabel(locale, step.title)}</a>
              ) : (
                <strong>{translateLabel(locale, step.title)}</strong>
              )}
              <span className="muted"> — {translateLabel(locale, step.explanation)}</span>
              {step.who && !step.done ? <span className="muted"> {step.who}</span> : null}
              {step.action === "resend_email_confirmation" && !step.done && list.resend_to ? (
                <>
                  {" "}
                  <button
                    type="button"
                    className="btn btn-quiet"
                    disabled={busy}
                    onClick={() => void resend(list.resend_to as string)}
                  >
                    {t("Send the link again")}
                  </button>
                  {resent ? (
                    <span className="muted" role="status">
                      {" "}
                      {resent}
                    </span>
                  ) : null}
                </>
              ) : null}
              </div>
            </li>
          );
        })}
      </ol>
      <div className="step-foot">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void putAway()}>
          {t("Put this list away")}
        </button>
        <p className="field-hint">
          {t("It goes for you only, and it goes by itself once every step that is not optional is done.")}
        </p>
      </div>
    </section>
  );
}

export function ConsoleHome() {
  const { t } = useT();
  return (
    <div className="stack">
      <h1>{t("Overview")}</h1>
      <TodayStrip />
      <GettingStartedList />
    </div>
  );
}

// PortalHome WAS HERE AND V24 DELETED IT, for the reason V16 gave when it
// deleted WorkerHome. It printed a heading, the signed-in identity and a "this
// surface is coming" note, which was honest at V4 and became a second front page
// the moment screens/Portal.tsx existed. The id `portal.home` did not move, so no
// link, nav entry or gallery cell changed.

// WorkerHome WAS HERE AND V16 DELETED IT. It printed a heading, the signed-in
// identity and a "this surface is coming" note, which was honest at V4 and became
// a second front page the moment screens/MyShifts.tsx existed. Deleting it was
// cheaper than keeping a stub nobody would remember to remove — and the id
// `worker.home` did not move, so no link, nav entry or gallery cell changed.
